import {
  compact, noop, contains, isNumber,
} from 'underscore';
import { endsWith } from 'underscore.string';
import PropTypes from 'prop-types';
import $ from 'jquery';
import { StatusDropdown } from '@bingads-webui-campaign-react/status-dropdown';
import React from 'react';
import { Callout } from '@bingads-webui-react/callout';
import { curlyTemplate } from '@bingads-webui-universal/primitive-utilities';
import { BiddingStrategyToBidParam } from '@bingads-webui-campaign/audience-campaign-shared';
import { CampaignType } from '@bingads-webui-campaign-react/shell-ui-common';
import { HelpPopup } from '@bingads-webui-react/help-popup';
import { formatPercentOneDecimal } from '@bingads-webui-campaign/recommendation-core';
import { isXandrCampaign, isXandrVideoAdsCampaign } from '@bingads-webui-campaign-react/constants';
import { SettingsItem } from './settings-item';
import { campaignClickHandler, getCampaignUrl, pageClickHandler } from './router-helper';
import { RepositoryConsumer } from './repository-consumer';
import { SharedBudgetConsumer } from './shared-budget-consumer';
import { CriteriaItems } from './criteria-items-content';
import { OverflowContent } from '../components/overflow-entity-item';
import { CampaignBudgetEditor } from '../editors/campaign-budget';
import { SharedBudgetEditor } from '../editors/shared-budget-editor';
import { AdGroupBidEditor } from '../editors/adgroup-bid-editor';
import { StartDateEditor } from '../editors/experiment-campaign/start-date';
import { EndDateEditor } from '../editors/experiment-campaign/end-date';
import { OptimizationScoreItem } from '../components/optimization-score-item';
import { NavigationItem } from '../components/navigation-item';

const getCpmText = (i18n, campaignSubType) => {
  if (isXandrCampaign(campaignSubType)) {
    return i18n.getString(_TL_('CPM for maximizing reach'));
  }

  return i18n.getString(_TL_('Manual CPM'));
};

const getBidStrategyMap = (i18n, campaignSubType, currentCampaignType) => ({
  ManualCpa: currentCampaignType === CampaignType.App ? i18n.getString(_TL_('Manual CPI')) : i18n.getString(_TL_('Manual CPA')),
  ManualCpc: i18n.getString(_TL_('Manual CPC')),
  ManualCpm: getCpmText(i18n, campaignSubType),
  ManualCpv: isXandrVideoAdsCampaign(campaignSubType) ? i18n.getString(_TL_('CPCV')) : i18n.getString(_TL_('Manual CPV')),
  EnhancedCpc: i18n.getString(_TL_('Enhanced CPC')),
  MaxClicks: i18n.getString(_TL_('Maximize Clicks')),
  MaxConversions: i18n.getString(_TL_('Maximize Conversions')),
  TargetCpa: i18n.getString(_TL_('Target CPA')),
  TargetRoas: i18n.getString(_TL_('Target ROAS')),
  MaxConversionValue: i18n.getString(_TL_('Maximize Conversion Value')),
  TargetImpressionShare: i18n.getString(_TL_('Target Impression Share')),
  Commission: i18n.getString(_TL_('Commission')),
  PercentCpc: i18n.getString(_TL_('Percent CPC')),
  CostPerSale: i18n.getString(_TL_('Cost per Sale')),
});

const getExperimentStatusMap = i18n => ({
  Active: i18n.getString(_TL_('Active')),
  Paused: i18n.getString(_TL_('Paused')),
  Creating: i18n.getString(_TL_('Creating')),
  CreationFailed: i18n.getString(_TL_('Creation Failed')),
  Ended: i18n.getString(_TL_('Completed')),
  Scheduled: i18n.getString(_TL_('Scheduled')),
  Graduating: i18n.getString(_TL_('Applying')),
  Promoting: i18n.getString(_TL_('Applying')),
  Graduated: i18n.getString(_TL_('Applied')),
  Promoted: i18n.getString(_TL_('Applied')),
  GraduateFailed: i18n.getString(_TL_('Apply Failed')),
  PromoteFailed: i18n.getString(_TL_('Apply Failed')),
});

const getExperimentTypeMap = i18n => ({
  TrafficBased: i18n.getString(_TL_('Search-based')),
  CookieBased: i18n.getString(_TL_('Cookie-based')),
});

const statusEntityItem = (i18n, { changeStatus, status }, isExperiment, ariaLabel) => ({
  className: 'summary-bar-status',
  renderEntityItem: () => (<StatusDropdown
    selectedStatus={{ id: status }}
    i18n={i18n}
    changeStatus={changeStatus}
    isExperiment={isExperiment}
    aria-label={ariaLabel}
    label={i18n.getString(_TL_('Status'))}
  />),
  noOverflow: true,
  canEdit: true,
});

const experimentStatusEntityItem = (i18n, { status }) => ({
  label: i18n.getString(_TL_('Experiment status')),
  className: 'summary-bar-experiment-status',
  canEdit: false,
  renderContent: () => (<span className="entity-text">{getExperimentStatusMap(i18n)[status]}</span>),
});

const experimentSplitOptionEntityItem = (i18n, { experimentType }) => ({
  label: i18n.getString(_TL_('Split option')),
  className: 'summary-bar-experiment-split-option',
  renderContent: () => (
    <span className="entity-text">
      {getExperimentTypeMap(i18n)[experimentType]}
      <HelpPopup
        aria-label={i18n.getString(_TL_('Learn more'))}
        topic="pop_BA_Experiments_Scorecard_SplitOptions"
      />
    </span>),
  canEdit: false,
});

const originalCampaignEntityItem = (i18n, { originalCampaignId, originalCampaignName, scope }) => ({
  label: i18n.getString(_TL_('Original campaign')),
  className: 'summary-bar-original-campaign',
  ariaLabel: i18n.getString(_TL_('Go to original campaign')),
  canEdit: true,
  onClick: event => campaignClickHandler(event, originalCampaignId, scope),
  renderContent: () => (
    <div className="entity-link">
      <OverflowContent
        tooltipPlacement="top"
        text={originalCampaignName}
        href={getCampaignUrl(originalCampaignId, scope)}
      />
    </div>
  ),
});

// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
const getPanelProps = ({ title, onClose, ...other }) => (isOpen, closePanel) => ({
  title,
  isOpen,
  onClose: () => {
    closePanel();
    if (onClose) {
      onClose();
    }
  },
  getChildren: () => (
    <div>
      <p>{`The ${title} is not implemented yet`}</p>
    </div>
  ),
  ...other,
});

const getCampaignBudgetEditorName = (experimentId, budget) => {
  if (experimentId) {
    return 'experiment-budget';
  }

  if (budget && budget.Id) {
    return 'shared-budget';
  }

  if (budget && budget.Amount) {
    return 'inline-budget';
  }
  return '';
};

const getTargetContainer = (cssSelector) => {
  const item = $(cssSelector);
  if (item.length > 0) {
    return item[0];
  }
  return null;
};

const getFocusLockProps = parentContainer => ({
  getAnchor: () => getTargetContainer(`${parentContainer} > div`),
});

const budgetEntityItem = ({
  i18n,
  budget,
  hasBudgetLandscape = false,
  canEdit,
  campaignType,
  campaignSubType,
  campaignId,
  summaryBarActivityLogger,
  experimentId = null,
  updateInlineBudget = noop,
  updateSharedBudget = noop,
  scope,
  permissions,
  siteMap,
}) => (
  {
    label: i18n.getString(_TL_('Budget')),
    className: 'summary-bar-budget',
    text: curlyTemplate(i18n.getString(_TL_('{{budget}}/day')))({ budget: budget.Amount.toFixed(2) }),
    ariaLabel: i18n.getString(_TL_('Campaign budget')),
    Editor: Callout,
    getEditorProps: (show, closeEditor) => ({
      title: budget && budget.Id ?
        curlyTemplate(i18n.getString(_TL_('Set shared budget: {{budgetName}}')))({
          budgetName: budget.Name,
        }) : i18n.getString(_TL_('Set campaign budget')),
      content: (<CampaignBudgetEditor
        experimentId={experimentId}
        budget={budget}
        campaignType={campaignType}
        campaignSubType={campaignSubType}
        campaignId={campaignId}
        onSave={closeEditor}
        onCancel={closeEditor}
        updateInlineBudget={updateInlineBudget}
        updateSharedBudget={updateSharedBudget}
        hasBudgetLandscape={hasBudgetLandscape}
        summaryBarActivityLogger={summaryBarActivityLogger}
        scope={scope}
        siteMap={siteMap}
        permissions={permissions}
        i18n={i18n}
      />),
      show,
      onClose: closeEditor,
      closeButton: true,
      target: null,
      placement: 'bottom',
      popoverId: `summary-bar-campaign-${getCampaignBudgetEditorName(experimentId, budget)}-editor`,
      popoverClassName: `summary-bar-campaign-${getCampaignBudgetEditorName(experimentId, budget)}-editor`,
      closeButtonAriaLabel: i18n.getString(_TL_('Close')),
      experimentId,
      focusLockContent: true,
      focusLockProps: getFocusLockProps('.summary-bar-budget'),
    }),
    canEdit,
  }
);

const optimizationScoreEntityItem = (i18n, osData, permissions) => ({
  className: 'summary-bar-optimization-score',
  text: osData && osData.OverallOptimizationScoreBar ?
    formatPercentOneDecimal(osData.OverallOptimizationScoreBar.Gained + osData.OverallOptimizationScoreBar.Dismissed, i18n) : '-',
  label: i18n.getString(_TL_('Optimization score')),
  ariaLabel: i18n.getString(_TL_('Optimization score')),
  renderEntityItem: () => (<OptimizationScoreItem i18n={i18n} osData={osData} permissions={permissions} />),
  canEdit: false,
});

const autoBidStrategyList = ['MaxClicks', 'MaxConversions', 'TargetCpa', 'TargetRoas', 'MaxConversionValue', 'TargetImpressionShare', 'CostPerSale'];
const bidEntityItem = ({
  i18n,
  bid,
  bidStrategy,
  displayBiddingStrategyType,
  canEdit,
  campaignType,
  updateBid,
  campaignSubType,
  summaryBarActivityLogger,
  permissions,
  dealMinimumBid,
}) => {
  let bidText;
  if (bidStrategy === 'CostPerSale') {
    bidText = i18n.getString(_TL_('Automatic'));
  } else if (contains(autoBidStrategyList, displayBiddingStrategyType)) {
    bidText = i18n.getString(_TL_('Auto'));
  } else {
    bidText = isNumber(bid) ? bid.toFixed(2) : '-';
  }

  return {
    label: i18n.getString(_TL_('Bid')),
    className: 'summary-bar-bid',
    text: bidText,
    ariaLabel: i18n.getString(_TL_('Bid')),
    Editor: Callout,
    getEditorProps: (show, closeEditor) => ({
      title: i18n.getString(_TL_('Set ad group bid')),
      content: (<AdGroupBidEditor
        onSave={closeEditor}
        onCancel={closeEditor}
        campaignSubType={campaignSubType}
        data={{ [BiddingStrategyToBidParam[bidStrategy] || 'SearchBid']: bid }}
        campaignType={campaignType}
        bidStrategy={bidStrategy}
        updateBid={updateBid}
        summaryBarActivityLogger={summaryBarActivityLogger}
        permissions={permissions}
        i18n={i18n}
        dealMinimumBid={dealMinimumBid}
      />),
      show,
      onClose: closeEditor,
      closeButton: true,
      target: null,
      placement: 'bottom',
      popoverId: 'summary-bar-adgroup-bid-editor',
      popoverClassName: 'summary-bar-adgroup-bid-editor',
      closeButtonAriaLabel: i18n.getString(_TL_('Close')),
      focusLockContent: true,
      focusLockProps: getFocusLockProps('.summary-bar-bid'),
    }),
    canEdit: canEdit && !contains(autoBidStrategyList, displayBiddingStrategyType),
  };
};

const isDateEditable = (isStartDate, { startDate }) => {
  if (isStartDate) {
    const isNotStarted = new Date(startDate) > new Date();

    return isNotStarted;
  }

  return true;
};

const parseDate = (datetime) => {
  if (!datetime) {
    return null;
  }

  const date = datetime.substr(0, 10).replace(/-/g, '/');

  return new Date(date);
};

const reformatDate = (datetime, i18n) => {
  const date = parseDate(datetime);

  if (!date) {
    return i18n.getString(_TL_('None'));
  }

  return i18n.formatDate(date, { timeZone: null, skeleton: 'yMd' });
};

const getDateText = (isStartDate, { startDate, endDate }, i18n) => {
  const datetime = isStartDate ? startDate : endDate;

  return reformatDate(datetime, i18n);
};

const startEndDate = (i18n, { startDate, endDate }, canEdit, changeDate, summaryBarActivityLogger, isStartDate = true) => ({
  label: isStartDate ? i18n.getString(_TL_('Start date')) : i18n.getString(_TL_('End date')),
  className: `summary-bar-${isStartDate ? 'start-date' : 'end-date'}`,
  text: getDateText(isStartDate, { startDate, endDate }, i18n),
  ariaLabel: isStartDate ? i18n.getString(_TL_('Start date')) : i18n.getString(_TL_('End date')),
  Editor: Callout,
  getEditorProps: (show, closeEditor) => ({
    title: isStartDate ? i18n.getString(_TL_('Set experiment campaign start date')) : i18n.getString(_TL_('Set experiment campaign end date')),
    content: isStartDate ? (<StartDateEditor
      onSave={closeEditor}
      onCancel={closeEditor}
      data={{ startDate, endDate }}
      changeDate={changeDate}
      summaryBarActivityLogger={summaryBarActivityLogger}
    />) : (<EndDateEditor
      onSave={closeEditor}
      onCancel={closeEditor}
      data={{ startDate, endDate }}
      changeDate={changeDate}
      summaryBarActivityLogger={summaryBarActivityLogger}
    />),
    show,
    onClose: closeEditor,
    closeButton: true,
    target: null,
    placement: 'bottom',
    popoverId: `summary-bar-experiment-campaign-${isStartDate ? 'start' : 'end'}-editor`,
    popoverClassName: `summary-bar-experiment-campaign-${isStartDate ? 'start' : 'end'}-editor`,
    closeButtonAriaLabel: i18n.getString(_TL_('Close')),
    focusLockContent: true,
    focusLockProps: getFocusLockProps(`.summary-bar-${isStartDate ? 'start-date' : 'end-date'}`),
  }),
  canEdit: canEdit && isDateEditable(isStartDate, { startDate }),
});

const isNoSettingsPage = (campaignSubType, isAdGroup) => campaignSubType === 'ShoppingSmartAds' && isAdGroup;
const isSettingsPage = scope => scope && scope.location && scope.location.pathname && endsWith(scope.location.pathname, 'settings');
const isAudiencesPage = scope => scope && scope.location && scope.location.pathname && endsWith(scope.location.pathname, 'audiences');

const criteriaItems = ({
  i18n,
  scope,
  canEdit,
  isAdGroup,
  siteMap,
  campaignSubType,
  currentCampaignType,
  // these are only for tests
  locations,
  audiences,
  devices,
  excludedLocationOnly,
}) => {
  const repositoryConsumer = new RepositoryConsumer({ scope, i18n });
  const {
    renderAdGroupAudience,
    renderCamapignAudience,
    renderCampaignLocationDevice,
    renderAdGroupLocationDevice,
  } = repositoryConsumer.criteriaRenderer;

  const renderCriteriaItem = ({
    targets,
    adGroupCriteriaRenderer,
    campaignCriteriaRenderer,
    isLocation = false,
  }) => {
    if (targets) {
      const RenderCriteriaItem = ({
        onClick, canEdit: canCriteriaItemEdit, ariaLabel,
      } = {}) => (<CriteriaItems
        targets={isLocation && excludedLocationOnly ? [] : targets}
        emptyValue={isLocation ? i18n.getString(_TL_('All locations')) : i18n.getString(_TL_('None'))}
        i18n={i18n}
        canEdit={canCriteriaItemEdit}
        ariaLabel={ariaLabel}
        onClick={onClick}
      />);

      RenderCriteriaItem.propTypes = {
        onClick: PropTypes.func.isRequired,
        canEdit: PropTypes.bool.isRequired,
        ariaLabel: PropTypes.string.isRequired,
      };

      return RenderCriteriaItem;
    } else if (isAdGroup) {
      return adGroupCriteriaRenderer();
    }
    return campaignCriteriaRenderer();
  };

  return [
    ...(isXandrCampaign(campaignSubType) && !isAdGroup) ? [] : [{
      label: i18n.getString(_TL_('Locations')),
      className: 'summary-bar-locations',
      renderContent: renderCriteriaItem({
        targets: locations,
        adGroupCriteriaRenderer: () => renderAdGroupLocationDevice(true),
        campaignCriteriaRenderer: () => renderCampaignLocationDevice(true),
        isLocation: true,
      }),
      canEdit: canEdit && !isSettingsPage(scope) && !isNoSettingsPage(campaignSubType, isAdGroup),
      ariaLabel: i18n.getString(_TL_('Edit Locations')),
      overflowItemText: i18n.getString(_TL_('Go to settings page')),
      Editor: NavigationItem,
      onNavigate: () => pageClickHandler(siteMap, scope, 'settings'),
      getEditorProps: shouldNavigate => ({ shouldNavigate, scope, siteMap }),
      excludedLocationOnly,
    }],
    ...((currentCampaignType === CampaignType.PerformanceMax || campaignSubType === 'ShoppingSmartAds' || (isXandrCampaign(campaignSubType) && !isAdGroup)) ? [] :
      [{
        label: i18n.getString(_TL_('Audiences')),
        className: 'summary-bar-audiences',
        renderContent: renderCriteriaItem({
          targets: audiences,
          adGroupCriteriaRenderer: () => renderAdGroupAudience,
          campaignCriteriaRenderer: () => renderCamapignAudience,
        }),
        ariaLabel: i18n.getString(_TL_('Edit Audiences')),
        canEdit: canEdit && !isAudiencesPage(scope),
        overflowItemText: i18n.getString(_TL_('Go to audiences page')),
        Editor: NavigationItem,
        onNavigate: () => pageClickHandler(siteMap, scope, 'audiences'),
        getEditorProps: shouldNavigate => ({
          shouldNavigate,
          targetPage: 'audiences',
          scope,
          siteMap,
        }),
      }, {
        label: i18n.getString(_TL_('Device')),
        className: 'summary-bar-devices',
        renderContent: renderCriteriaItem({
          targets: devices,
          adGroupCriteriaRenderer: () => renderAdGroupLocationDevice(false, campaignSubType),
          campaignCriteriaRenderer: () => renderCampaignLocationDevice(false),
        }),
        ariaLabel: i18n.getString(_TL_('Edit Devices')),
        overflowItemText: i18n.getString(_TL_('Go to settings page')),
        Editor: NavigationItem,
        onNavigate: () => pageClickHandler(siteMap, scope, 'settings'),
        getEditorProps: shouldNavigate => ({ shouldNavigate, scope, siteMap }),
        canEdit: canEdit && !isSettingsPage(scope),
      }]),
  ];
};


const isBidStrategyEditable = ({
  isAdGroup,
  currentCampaignType,
  scope,
  campaignSubType,
  bidStrategy,
}) => {
  if (currentCampaignType === CampaignType.App) {
    return false;
  }
  if (bidStrategy === 'CostPerSale') {
    return false;
  }
  if (isAdGroup) {
    return !isSettingsPage(scope) && !isNoSettingsPage(campaignSubType, isAdGroup) && (currentCampaignType === CampaignType.Default || currentCampaignType === CampaignType.Audience ||
    (currentCampaignType === CampaignType.Shopping)) && campaignSubType !== 'ShoppingSponsoredProductAd';
  }
  return !isSettingsPage(scope) &&
    currentCampaignType !== CampaignType.Audience &&
    campaignSubType !== 'ShoppingSponsoredProductAd' &&
    (currentCampaignType !== CampaignType.Shopping || campaignSubType !== 'ShoppingSmartAds');
};

const isAudienceFeedBasedCampaign = ({ currentCampaignType, campaignSubType }) => (
  currentCampaignType === CampaignType.Audience && campaignSubType === 'AudienceFeedBased'
);

const isOptimizedTargetingEnabled = ({ currentCampaignType, isAdGroup }) => isAdGroup
    && currentCampaignType === CampaignType.Audience;

const isOptimizedTargetingEditable = ({
  isAdGroup,
  scope,
  campaignSubType,
}) => !isSettingsPage(scope) && !isNoSettingsPage(campaignSubType, isAdGroup);

const autoTargetingEntityItem = ({
  isAdGroup,
  siteMap,
  canEdit,
  scope,
  i18n,
  campaignSubType,
  predictiveTargeting,
}) => ({
  label: i18n.getString(_TL_('Predictive targeting')),
  className: 'summary-bar-predictive-targeting',
  text: predictiveTargeting ? i18n.getString(_TL_('On')) : i18n.getString(_TL_('Off')),
  ariaLabel: i18n.getString(_TL_('Predictive targeting')),
  Editor: NavigationItem,
  overflowItemText: i18n.getString(_TL_('Go to settings page')),
  getEditorProps: shouldNavigate => ({ shouldNavigate, scope, siteMap }),
  onNavigate: () => pageClickHandler(siteMap, scope, 'settings'),
  canEdit: canEdit && isOptimizedTargetingEditable({ isAdGroup, scope, campaignSubType }),
});

export const sharedEntityItems = ({
  bidStrategy,
  scope,
  campaignSubType,
  permissions,
  siteMap,
  canEdit,
  currentCampaignType,
  isAdGroup = false,
  i18n,
  predictiveTargeting,
  ...rest
}) => [
  ...(isXandrCampaign(campaignSubType) && isAdGroup) ? [] : [{
    label: i18n.getString(_TL_('Bid strategy')),
    className: 'summary-bar-bid-strategy',
    text: getBidStrategyMap(i18n, campaignSubType, currentCampaignType)[bidStrategy],
    ariaLabel: i18n.getString(_TL_('Bid strategy')),
    Editor: NavigationItem,
    overflowItemText: i18n.getString(_TL_('Go to settings page')),
    getEditorProps: shouldNavigate => ({ shouldNavigate, scope, siteMap }),
    onNavigate: () => pageClickHandler(siteMap, scope, 'settings'),
    canEdit: canEdit && isBidStrategyEditable({
      isAdGroup,
      permissions,
      currentCampaignType,
      scope,
      campaignSubType,
      bidStrategy,
    }),
  }],
  ...compact(criteriaItems({
    i18n,
    scope,
    canEdit,
    isAdGroup,
    siteMap,
    campaignSubType,
    currentCampaignType,
    ...rest,
  })),
  !isAudienceFeedBasedCampaign({ currentCampaignType, campaignSubType }) &&
  isOptimizedTargetingEnabled({ isAdGroup, currentCampaignType }) ? autoTargetingEntityItem({
      isAdGroup, siteMap, canEdit, scope, i18n, predictiveTargeting, campaignSubType,
    }) : null,
  {
    className: 'overflow-entity-items',
    noOverflow: true,
  },
  campaignSubType !== 'ShoppingSmartAds' || !isAdGroup ? {
    className: 'summary-bar-settings',
    noOverflow: true,
    canEdit: true,
    renderEntityItem: () => (
      <SettingsItem i18n={i18n} scope={scope} siteMap={siteMap} />
    ),
  } : null,
].filter(item => item != null);

export const sharedBudgetItems = ({
  scope,
  i18n,
  canEdit,
  updateSharedBudget,
  summaryBarActivityLogger,
  budget,
}) => {
  const repositoryConsumer = new SharedBudgetConsumer({ scope, i18n, budget });
  const {
    renderSharedBudgetAmount,
    renderSharedBudgetType,
  } = repositoryConsumer.criteriaRenderer;

  return [{
    label: i18n.getString(_TL_('Budget')),
    className: 'summary-bar-budget',
    renderContent: renderSharedBudgetAmount,
    ariaLabel: i18n.getString(_TL_('Budget')),
    canEdit,
    Editor: Callout,
    getEditorProps: (show, closeEditor) => ({
      title: i18n.getString(_TL_('Set shared budget')),
      content: (<SharedBudgetEditor scope={scope} onSave={closeEditor} onCancel={closeEditor} data={{ Amount: budget.Amount }} updateSharedBudget={updateSharedBudget} summaryBarActivityLogger={summaryBarActivityLogger} helpId="pop_BA_SharedBudget_Budgets_Budget" />),
      show,
      onClose: closeEditor,
      closeButton: true,
      target: null,
      placement: 'bottom',
      popoverId: 'summary-bar-shared-budget-editor',
      popoverClassName: 'summary-bar-shared-budget-editor',
      closeButtonAriaLabel: i18n.getString(_TL_('Close')),
      focusLockContent: true,
      focusLockProps: getFocusLockProps('.summary-bar-budget'),
    }),
  }, {
    label: i18n.getString(_TL_('Budget type')),
    className: 'summary-bar-budget-type',
    renderContent: renderSharedBudgetType,
    ariaLabel: i18n.getString(_TL_('Budget type')),
    canEdit: false,
  }];
};

export const campaignEntityItems = ({
  status,
  budget,
  i18n,
  changeStatus,
  canEdit,
  currentCampaignType,
  campaignSubType,
  id,
  updateInlineBudget,
  updateSharedBudget,
  hasBudgetLandscape,
  summaryBarActivityLogger,
  scope,
  permissions,
  siteMap,
  osData,
  ...other
}) => {
  const entityItems = [
    statusEntityItem(i18n, { changeStatus, status }, false, i18n.getString(_TL_('Campaign status'))),
    budgetEntityItem({
      i18n,
      budget,
      hasBudgetLandscape,
      canEdit,
      campaignType: currentCampaignType,
      campaignSubType,
      campaignId: id,
      summaryBarActivityLogger,
      experimentId: null,
      updateInlineBudget,
      updateSharedBudget,
      scope,
      permissions,
      siteMap,
    }),
  ];
  const isOptimizationScoreOn = permissions.dynamic.OptimizationScore && currentCampaignType !== CampaignType.PerformanceMax;
  if (isOptimizationScoreOn) {
    entityItems.push(optimizationScoreEntityItem(i18n, osData, permissions));
  }
  entityItems.push(...sharedEntityItems({
    i18n, ...other, canEdit, currentCampaignType, scope, permissions, siteMap, campaignSubType,
  }));

  return entityItems;
};

export const experimentCampaignEntityItems = ({
  status,
  startDate,
  endDate,
  originalCampaignName,
  originalCampaignId,
  scope,
  permissions,
  siteMap,
  budget,
  i18n,
  experimentType,
  canEdit,
  currentCampaignType,
  campaignSubType,
  id,
  experimentId,
  summaryBarActivityLogger,
  changeStartDate,
  changeEndDate,
  ...other
}) => [
  experimentStatusEntityItem(i18n, { status }),
  startEndDate(i18n, { startDate, endDate }, canEdit, changeStartDate, summaryBarActivityLogger),
  startEndDate(i18n, { startDate, endDate }, canEdit, changeEndDate, summaryBarActivityLogger, false),
  experimentSplitOptionEntityItem(i18n, { experimentType }),
  originalCampaignEntityItem(i18n, { originalCampaignId, originalCampaignName, scope }),
  budgetEntityItem({
    i18n,
    budget,
    canEdit,
    campaignType: currentCampaignType,
    campaignSubType,
    campaignId: id,
    summaryBarActivityLogger,
    experimentId,
    scope,
    permissions,
    siteMap,
  }),
  ...sharedEntityItems({
    i18n, ...other, canEdit, currentCampaignType, scope, permissions, siteMap, campaignSubType,
  }),
];

export const adGroupEntityItems = ({
  status,
  bid,
  bidStrategy,
  displayBiddingStrategyType,
  i18n,
  changeStatus,
  canEdit,
  updateBid,
  currentCampaignType,
  permissions,
  scope,
  siteMap,
  summaryBarActivityLogger,
  predictiveTargeting,
  dealMinimumBid,
  ...other
}) => [
  statusEntityItem(i18n, { changeStatus, status }, false, i18n.getString(_TL_('Ad group status'))),
  bidEntityItem({
    i18n,
    bid,
    bidStrategy,
    displayBiddingStrategyType,
    canEdit,
    campaignType: currentCampaignType,
    campaignSubType: other.campaignSubType,
    updateBid,
    summaryBarActivityLogger,
    permissions,
    dealMinimumBid,
  }),
  ...sharedEntityItems({
    i18n, ...other, canEdit, currentCampaignType, scope, permissions, siteMap, isAdGroup: true, bidStrategy, predictiveTargeting,
  }),
];

export const experimentAdGroupEntityItems = ({
  status,
  bid,
  bidStrategy,
  displayBiddingStrategyType,
  i18n,
  changeStatus,
  canEdit,
  updateBid,
  currentCampaignType,
  permissions,
  scope,
  siteMap,
  summaryBarActivityLogger,
  ...other
}) => [
  statusEntityItem(i18n, { changeStatus, status }, true, i18n.getString(_TL_('Ad group status'))),
  bidEntityItem({
    i18n,
    bid,
    bidStrategy,
    displayBiddingStrategyType,
    canEdit,
    campaignType: currentCampaignType,
    updateBid,
    summaryBarActivityLogger,
    permissions,
  }),
  ...sharedEntityItems({
    i18n, ...other, canEdit, currentCampaignType, scope, permissions, siteMap, isAdGroup: true, bidStrategy,
  }),
];
